import type { MouseEvent } from 'react';
import { useNavigate, useLocation, useInRouterContext } from 'react-router-dom';
import { useZoomStore } from '@/shared/store/zoomStore';
import { IconOpenDetail } from './Icon';

// The in-box "open the record" affordance, shared by AsyncPicker and Select so
// both fields look and behave the same (the reference is the Sales Order
// header's Customer field).
//
// One structure for both: a wrapper box (border, background, focus ring) holds
// the trigger <button role="combobox"> and, as its SIBLING, this divider + icon
// link. The link is never inside the trigger (no interactive element nested in a
// button). It is a real <a href={openTo}>: click, middle-click and screen-reader
// browse work; tabIndex -1 and no data-travel keep it out of both the Tab order and the arrow travel (the trigger is the field's one stop, and arrows on it must travel on, not land on the icon), and Alt+Enter on the
// trigger is the keyboard route (the trigger advertises it via aria-keyshortcuts).

// An editable box shows focus by its border only (no ring); the red border of an
// invalid box is set by the caller with focus-within:border-red-500.
export const DRILL_BOX_FOCUS = 'focus-within:border-primary';
// An inert (grey) box focuses the way InlineEdit's READONLY box does: it keeps
// its border, turns to the surface background and takes the inset primary ring
// for keyboard focus only (a mouse click on the box draws no ring).
export const DRILL_BOX_INERT_FOCUS = 'has-[:focus-visible]:bg-surface has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-inset has-[:focus-visible]:ring-primary/70';
// The trigger inside the wrapper: transparent, the wrapper draws the box.
export const DRILL_TRIGGER =
  'flex-1 min-w-0 self-stretch bg-transparent border-0 p-0 text-left inline-flex items-center gap-2 outline-none';

export const DRILL_DIVIDER = 'shrink-0 w-px h-4 bg-border-light';
const ICON_BASE = 'shrink-0 inline-flex rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-primary';
export const DRILL_ICON_ACTIVE = `${ICON_BASE} text-secondary hover:text-primary-text focus-visible:text-primary transition-colors cursor-pointer`;
export const DRILL_ICON_DIM = `${ICON_BASE} text-muted/40`;

// Opens `openTo`, pushing the current page onto the zoom stack as a crumb
// labelled `fromLabel` so the back-stack works. Outside a router (a plain
// Select in an isolated test) it does nothing for a click, so the <a> follows
// its href; Alt+Enter falls back to a plain location change.
export function useOpenRecord(openTo: string | undefined, fromLabel: string | undefined) {
  // Whether a component sits inside a router never changes during its life, so
  // the hook order is stable; the router hooks throw outside one.
  const inRouter = useInRouterContext();
  const nav = inRouter ? useNavigate() : null; // eslint-disable-line react-hooks/rules-of-hooks
  const location = inRouter ? useLocation() : null; // eslint-disable-line react-hooks/rules-of-hooks
  const pushCrumb = useZoomStore((s) => s.push);
  return function openRecord(e?: MouseEvent) {
    if (!openTo) return;
    // Modified / non-primary clicks keep the browser's own link behaviour.
    if (e && (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)) return;
    if (!nav || !location) {
      if (!e) window.location.assign(openTo);
      return;
    }
    e?.preventDefault(); e?.stopPropagation();
    pushCrumb({ path: location.pathname + location.search, label: fromLabel || '' });
    nav(openTo);
  };
}

type Props = {
  openTo?: string;
  openLabel?: string;
  // True when there is a record to open (openTo and a value).
  active: boolean;
  onOpen: (e?: MouseEvent) => void;
};

export function DrillAffordance({ openTo, openLabel = 'Open record', active, onOpen }: Props) {
  return (
    <>
      <span aria-hidden="true" className={DRILL_DIVIDER} />
      {active ? (
        <a
          href={openTo}
          tabIndex={-1}
          title={openLabel}
          aria-label={openLabel}
          onClick={onOpen}
          className={DRILL_ICON_ACTIVE}
        >
          <IconOpenDetail size={13} />
        </a>
      ) : (
        <a aria-disabled="true" aria-label={openLabel} title="Pick a value to open" className={DRILL_ICON_DIM}>
          <IconOpenDetail size={13} />
        </a>
      )}
    </>
  );
}
